import React, { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { makeStyles } from 'tss-react/mui';
import { sendAnalytics } from '../../../analytics/functions';
import { createToolbarEvent } from '../../../analytics/AnalyticsEvents';
import { IconInterpreting } from '../../../base/icons/svg';
import Button from '../../../base/ui/components/web/Button';
import { BUTTON_TYPES } from '../../../base/ui/constants.any';
import { showNotification } from '../../../notifications/actions';
import { NOTIFICATION_TIMEOUT_TYPE, NOTIFICATION_TYPE } from '../../../notifications/constants';
import { queueService } from '../../../interpreter-queue/InterpreterQueueService';
import { canRequestP2PInterpreter } from '../../../interpreter-queue/p2pInterpreterEligibility';
import { p2pInterpreterRequestTarget } from '../../../interpreter-queue/p2pInterpreterRequest';
import { getPersistentJson } from '../../../vrs-auth/storage';
import { isCompactCallBar } from '../../clientCallActions.web';
let requestSequence = 0;
const UNAVAILABLE_NOTICE_ID = 'p2p-interpreter-unavailable';
/**
 * Asks for an interpreter in this call. The button and the notice's
 * "Try again" both send it (one tap, Robert 2026-11-02). False when this
 * device has no call to ask for.
 */
function sendP2PInterpreterRequest() {
    const call = getPersistentJson('vrs_active_call');
    if (!call?.callId || !call.roomName) {
        return false;
    }
    sendAnalytics(createToolbarEvent('p2p.interpreter.requested'));
    queueService.sendP2PRequestInterpreter(call.callId, call.roomName, `p2p-btn-${Date.now()}-${++requestSequence}`, p2pInterpreterRequestTarget());
    return true;
}
const useStyles = makeStyles()(theme => ({
    wrapper: { display: 'inline-flex', margin: '0 8px' },
    // A narrow bar: an icon key like the bar's others.
    compactWrapper: {
        display: 'inline-flex',
        margin: 0
    },
    compactKey: {
        height: 48,
        padding: 0,
        width: 48
    },
    cta: {
        background: '#15803D',
        border: '1px solid #4ADE80',
        '&:hover': { background: '#166534' },
        '&:focus-visible': { outline: `2px solid ${theme.palette.focus01}`, outlineOffset: 2 }
    },
    pending: { background: '#166534', border: '1px solid #86EFAC' }
}));
/**
 * The P2P in-call "interpreting" button: centered green affordance, visible
 * only to the call originator, pulling a terp into the LIVE room. Server
 * contract: p2p_request_interpreter → p2p_interpreter_requested ack,
 * p2p_interpreter_joined / p2p_interpreter_unavailable verdicts.
 */
export default function P2PInterpretingButton() {
    const { t } = useTranslation();
    const { classes, cx } = useStyles();
    const dispatch = useDispatch();
    const [pending, setPending] = useState(false);
    const [joined, setJoined] = useState(false);
    const [eligible, setEligible] = useState(canRequestP2PInterpreter);
    const [callId, setCallId] = useState('');
    const compact = useSelector(isCompactCallBar);
    useEffect(() => {
        setEligible(canRequestP2PInterpreter());
        const call = getPersistentJson('vrs_active_call');
        setCallId(typeof call?.callId === 'string' ? call.callId : '');
    }, []);
    useEffect(() => {
        if (!eligible) {
            return;
        }
        const notify = (title, description) => dispatch(showNotification({
            descriptionKey: undefined,
            description,
            title,
            titleKey: undefined
        }, NOTIFICATION_TIMEOUT_TYPE.MEDIUM));
        // An error notice in the call: the text, and one way out. It stays
        // until the person acts or closes it.
        const notifyError = (title, retry) => dispatch(showNotification({
            appearance: NOTIFICATION_TYPE.ERROR,
            ...retry ? {
                customActionHandler: [() => {
                        if (sendP2PInterpreterRequest()) {
                            setPending(true);
                        }
                        return true;
                    }],
                customActionNameKey: ['inlineDialogFailure.retry']
            } : {},
            descriptionKey: undefined,
            hideErrorSupportLink: true,
            title,
            titleKey: undefined,
            uid: UNAVAILABLE_NOTICE_ID
        }, NOTIFICATION_TIMEOUT_TYPE.STICKY));
        const onJoined = (data) => {
            setPending(false);
            setJoined(true);
            // One short info line: "Interpreter #1042 joined." (Robert, 2026-11-02).
            notify(data?.interpreterName
                ? `${data.interpreterName} joined.`
                : t('toolbar.interpreterJoined', 'Interpreter joined the call'));
        };
        const onUnavailable = (data) => {
            setPending(false);
            // A revoked region grant withdrew it (docs/regional-queue-routing.md):
            // the server says why, and asking again does not help.
            if (data?.code === 'SERVICE_REGION_REVOKED' && data.message) {
                notifyError(data.message, false);
                return;
            }
            notifyError(t('toolbar.noInterpreterAvailable', 'No interpreter is available.'), true);
        };
        const onError = (data) => {
            if (data?.operation !== 'p2p_request_interpreter') {
                return;
            }
            setPending(false);
            if (data.code === 'P2P_INTERPRETER_PRESENT') {
                setJoined(true);
            }
            else if (data.code === 'ALREADY_REQUESTED') {
                setPending(true);
            }
            else {
                notifyError(data.message || t('toolbar.requestFailed', 'Request failed'), false);
            }
        };
        queueService.on('p2pInterpreterJoined', onJoined);
        queueService.on('p2pInterpreterUnavailable', onUnavailable);
        queueService.on('error', onError);
        return () => {
            queueService.off('p2pInterpreterJoined', onJoined);
            queueService.off('p2pInterpreterUnavailable', onUnavailable);
            queueService.off('error', onError);
        };
    }, [eligible, dispatch, t]);
    const press = useCallback(() => {
        if (pending || joined) {
            return;
        }
        if (sendP2PInterpreterRequest()) {
            setPending(true);
        }
    }, [pending, joined]);
    if (!eligible || joined || !callId) {
        return null;
    }
    // Button draws the icon (<Icon src = { icon } />), so it gets the icon
    // itself. An <Icon> element there crashed the caller's room (2026-09-25).
    return (React.createElement("div", { className: compact ? classes.compactWrapper : classes.wrapper },
        React.createElement(Button, { accessibilityLabel: t('toolbar.requestInterpreterInCall', 'Call an interpreter into this call'), className: cx(pending ? classes.pending : classes.cta, compact && classes.compactKey), icon: IconInterpreting, label: compact ? undefined : pending
                ? t('toolbar.requestingInterpreter', 'Requesting…')
                : t('toolbar.interpreter', 'Interpreter'), onClick: press, type: BUTTON_TYPES.PRIMARY })));
}
